import type { GameClient } from "../controller/GameClient";
import { EventProcessor } from "../model/EventProcessor";
import { LocalGameState } from "../model/LocalGameState";
import type { OutboundMessage, ActionPromptMessage } from "./OutboundMessage";
import type { InboundMessage } from "./InboundMessage";

const WS_BASE_URL = (import.meta.env.VITE_UMPIRE_WS_URL as string | undefined) ?? "ws://localhost:8080";

export class GameSession {
  readonly state: LocalGameState;
  private readonly eventProcessor: EventProcessor;
  private ws: WebSocket | null = null;
  private lastPrompt: ActionPromptMessage | null = null;
  private promptResponseSent = false;

  constructor(
    private readonly gameId: string,
    private readonly token: string,
    private gameClient: GameClient,
    state?: LocalGameState,
  ) {
    this.state = state ?? new LocalGameState();
    this.eventProcessor = new EventProcessor(this.state, gameClient);
  }

  connect(): void {
    const url = `${WS_BASE_URL}/ws/games/${this.gameId}?token=${this.token}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log(`[Connected to game ${this.gameId}]`);
    };

    this.ws.onmessage = (event: MessageEvent) => {
      let msg: OutboundMessage;
      try {
        msg = JSON.parse(event.data as string) as OutboundMessage;
      } catch (e) {
        console.warn("[Warning] Could not parse server message:", e);
        return;
      }
      console.log(`[GameSession] ← ${msg.type}`, msg);
      this.dispatch(msg);
    };

    this.ws.onclose = (event: CloseEvent) => {
      console.log(`[Connection closing: ${event.reason}]`);
    };

    this.ws.onerror = (event: Event) => {
      console.error("[Connection error]", event);
    };
  }

  setGameClient(client: GameClient): void {
    this.gameClient = client;
    this.eventProcessor.setGameClient(client);
    // If a prompt arrived while the old client was active (e.g. waiting room client
    // that always returns null) and no response was sent, replay it now.
    if (this.lastPrompt && !this.promptResponseSent) {
      console.log(`[GameSession] Replaying unanswered prompt: ${this.lastPrompt.action}`);
      this.promptAndSend(this.lastPrompt);
    }
  }

  disconnect(): void {
    this.ws?.close(1000);
    this.ws = null;
  }

  sendMessage(msg: InboundMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      console.warn("[GameSession] Cannot send — WebSocket not open");
    }
  }

  private dispatch(msg: OutboundMessage): void {
    switch (msg.type) {
      case "GAME_STATE_SNAPSHOT":
        if (msg.homePlayerInfo) for (const id of Object.keys(msg.homePlayerInfo)) { if (!this.state.homePlayerIds.includes(id)) this.state.homePlayerIds.push(id); }
        if (msg.awayPlayerInfo) for (const id of Object.keys(msg.awayPlayerInfo)) { if (!this.state.awayPlayerIds.includes(id)) this.state.awayPlayerIds.push(id); }
        if (msg.homeTeamId) this.state.homeTeamId = msg.homeTeamId;
        if (msg.awayTeamId) this.state.awayTeamId = msg.awayTeamId;
        const applyPlayerInfo = (id: string, info: { name: string; role: string; mov: number; str: number; dex: number; skills: string[] }, teamId: string) => {
          this.state.playerNames.set(id, info.name);
          const existing = this.state.players.get(id);
          this.state.players.set(id, {
            playerId: id,
            label: existing?.label ?? "",
            teamId,
            role: info.role,
            mov: info.mov,
            str: info.str,
            dex: info.dex,
            skills: info.skills,
          });
        };
        for (const [id, info] of Object.entries(msg.homePlayerInfo ?? {})) {
          applyPlayerInfo(id, info, this.state.homeTeamId ?? "");
        }
        for (const [id, info] of Object.entries(msg.awayPlayerInfo ?? {})) {
          applyPlayerInfo(id, info, this.state.awayTeamId ?? "");
        }
        const playerStates = msg.driveState?.downState?.playerStates;
        if (playerStates) {
          for (const [id, ps] of Object.entries(playerStates)) {
            this.state.movementRemaining.set(id, ps.movementPointsRemaining);
          }
        }
        this.state.phase = msg.phase;
        this.state.gameClock = msg.gameClock;
        this.gameClient.onSnapshot(msg, this.state);
        break;

      case "EVENT_BATCH":
        this.eventProcessor.applyBatch(msg.events);
        break;

      case "ACTION_PROMPT":
        this.lastPrompt = msg;
        this.promptResponseSent = false;
        this.promptAndSend(msg);
        break;

      case "FORMATION_ACCEPTED":
        this.lastPrompt = null;
        this.gameClient.onFormationAccepted();
        break;

      case "ERROR":
        this.gameClient.onError(msg.error);
        // Re-prompt if server rejected the last action and is still waiting.
        if (this.lastPrompt) {
          console.log(`[Retrying: ${this.lastPrompt.action}]`);
          this.promptAndSend(this.lastPrompt);
        }
        break;
    }
  }

  private promptAndSend(prompt: ActionPromptMessage): void {
    this.gameClient
      .onActionPrompt(prompt, this.state)
      .then((response) => {
        if (response !== null) {
          this.promptResponseSent = true;
          this.sendMessage(response);
        }
      })
      .catch((err: unknown) => {
        console.error(`[Error handling prompt '${prompt.action}']:`, err);
      });
  }
}
